import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

// All backend endpoints are under /v0.1. Auth base for convenience.
export const AUTH_BASE = '/v0.1/auth'

// In-memory CSRF token. Backend stores it in a non-httpOnly cookie and rotates
// server-side after each validated mutation without writing a new response cookie.
let csrfTokenValue: string | null = null

export function getCsrfToken(): string | null {
  return csrfTokenValue
}

export function setCsrfToken(token: string | null): void {
  csrfTokenValue = token
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

// Seed from cookie on module init (browser only).
if (typeof document !== 'undefined') {
  const cookieCsrf = readCookie('csrf_token')
  if (cookieCsrf) setCsrfToken(cookieCsrf)
}

// Request interceptor: attach X-CSRF-Token on mutating methods.
api.interceptors.request.use((config) => {
  const method = (config.method?.toLowerCase() ?? '').toString()
  if (csrfTokenValue && ['post', 'put', 'patch', 'delete'].includes(method)) {
    config.headers['X-CSRF-Token'] = csrfTokenValue
  }
  return config
})

// Response interceptor: re-read the CSRF cookie after every response so the
// in-memory token stays in sync with the server-rotated value.
api.interceptors.response.use(
  (response) => {
    if (typeof document !== 'undefined') {
      const newCsrf = readCookie('csrf_token')
      if (newCsrf && newCsrf !== csrfTokenValue) {
        setCsrfToken(newCsrf)
      }
    }
    return response
  },
  (error) => {
    // Handle auth errors globally — redirect to login on 401/403
    const status = error.response?.status
    if (typeof document !== 'undefined') {
      const newCsrf = readCookie('csrf_token')
      if (newCsrf && newCsrf !== csrfTokenValue) {
        setCsrfToken(newCsrf)
      }
    }
    if (status === 401) {
      console.warn('api: 401 unauthorized — session expired')
    }
    return Promise.reject(error)
  },
)
